import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Mail, Phone, StickyNote } from "lucide-react";
import { api, usePortalAuth, extractErr } from "@/portal/PortalAuth";
import { PortalHeader, ChangePasswordGate, btn, ghost, card, inp } from "@/portal/PortalShell";
import Bookings from "@/portal/Bookings";
import Payments from "@/portal/Payments";
import LiveChatInbox from "@/portal/LiveChatInbox";
import Inquiries from "@/portal/Inquiries";
import EmailSenderSettings from "@/portal/EmailSenderSettings";

const TABS = ["Dashboard", "Bookings", "Payments", "Live Chat", "Inquiries", "Leads", "Active Guests", "Requests", "Extensions", "Users", "Notifications"];
const LEAD_STATUSES = ["New", "Contacted", "Follow-Up Needed", "Qualified", "Converted", "Closed", "Not a Fit"];
const REQ_STATUSES = ["Received", "Assigned", "In Progress", "Waiting on Guest", "Resolved", "Closed"];
const ROLES_ALL = ["admin", "manager", "reservations", "housekeeping", "maintenance", "homeowner", "guest"];

export default function ManagementDashboard() {
  return <ChangePasswordGate><Inner /></ChangePasswordGate>;
}

function Inner() {
  const { user } = usePortalAuth();
  const chatDeepLink = new URLSearchParams(window.location.search).get("chat");
  const [tab, setTab] = useState(chatDeepLink ? "Live Chat" : "Dashboard");
  const [paymentsView, setPaymentsView] = useState("Reservations");
  const [chatUnread, setChatUnread] = useState(0);
  const [newInquiries, setNewInquiries] = useState(0);
  const isAdmin = user.role === "admin";
  const canCRM = ["admin", "manager", "reservations"].includes(user.role);
  const canChat = ["admin", "manager"].includes(user.role);
  const openTemplates = () => { setPaymentsView("Agreement Templates"); setTab("Payments"); };

  useEffect(() => {
    if (!canChat) return;
    const tick = () => api.get("/admin/chat/unread-count").then(({ data }) => setChatUnread(data.unread || 0)).catch(() => {});
    tick(); const t = setInterval(tick, 15000); return () => clearInterval(t);
  }, [canChat]);

  useEffect(() => {
    if (!canCRM) return;
    const tick = () => api.get("/admin/inquiries/count").then(({ data }) => setNewInquiries(data.new || 0)).catch(() => {});
    tick(); const t = setInterval(tick, 30000); return () => clearInterval(t);
  }, [canCRM]);

  const availableTabs = TABS.filter((t) => {
    if (t === "Users" || t === "Notifications") return isAdmin;
    if (t === "Live Chat") return canChat;
    if (t === "Bookings" || t === "Payments") return canCRM; // staff + admin
    if (t === "Inquiries" || t === "Leads" || t === "Active Guests" || t === "Extensions") return canCRM;
    return true;
  });

  return (
    <section data-testid="mgmt-dashboard" className="px-6 md:px-12 pt-28 md:pt-32 pb-24">
      <div className="max-w-6xl mx-auto">
        <PortalHeader subtitle="Management Portal" title="Operations" />
        <div className="flex flex-wrap gap-2 mb-6" role="tablist">
          {availableTabs.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
              className={`rounded-full px-4 py-2 text-sm border transition-colors inline-flex items-center gap-1.5 ${tab === t ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-white text-[#44403C] border-[#E7E5E4] hover:bg-[#F5F5F4]"}`}>
              {t}
              {t === "Live Chat" && chatUnread > 0 && <span data-testid="tab-chat-unread" className="text-[10px] bg-[#8A5A2B] text-white rounded-full px-1.5 py-0.5 leading-none">{chatUnread}</span>}
              {t === "Inquiries" && newInquiries > 0 && <span data-testid="tab-inquiries-new" className="text-[10px] bg-[#8A5A2B] text-white rounded-full px-1.5 py-0.5 leading-none">{newInquiries}</span>}
            </button>
          ))}
        </div>
        {canCRM && <TemplateApprovalBanner onOpen={openTemplates} tab={tab} />}
        {tab === "Dashboard" && <Summary />}
        {tab === "Bookings" && canCRM && <Bookings />}
        {tab === "Payments" && canCRM && <Payments view={paymentsView} onViewChange={setPaymentsView} />}
        {tab === "Live Chat" && canChat && <LiveChatInbox />}
        {tab === "Inquiries" && canCRM && <Inquiries />}
        {tab === "Leads" && canCRM && <Leads />}
        {tab === "Active Guests" && canCRM && <Guests />}
        {tab === "Requests" && <Requests />}
        {tab === "Extensions" && canCRM && <Extensions />}
        {tab === "Users" && isAdmin && <Users />}
        {tab === "Notifications" && isAdmin && <Notifications />}
      </div>
    </section>
  );
}

function TemplateApprovalBanner({ onOpen, tab }) {
  const [drafts, setDrafts] = useState([]);
  const load = () => api.get("/admin/booking/agreement-templates").then((r) => setDrafts((r.data || []).filter((t) => t.status !== "approved"))).catch(() => {});
  useEffect(() => { load(); }, [tab]);
  if (!drafts.length) return null;
  return (
    <div data-testid="template-approval-banner" className="mb-6 rounded-xl border border-[#FDE68A] bg-[#FEF3C7] p-4 text-sm text-[#92400E] flex flex-wrap items-center justify-between gap-3">
      <p>
        <strong>{drafts.length} stay agreement template{drafts.length > 1 ? "s" : ""} awaiting approval:</strong> {drafts.map((t) => t.title).join(" · ")}.
        Guests cannot sign — and no payment link can be issued — until each template is reviewed and approved.
      </p>
      <button onClick={onOpen} data-testid="template-approval-open" className={btn + " text-xs"}>Review & approve templates</button>
    </div>
  );
}

function Summary() {
  const [c, setC] = useState({});
  useEffect(() => { api.get("/management/dashboard").then((r) => setC(r.data)).catch(() => {}); }, []);
  const tiles = [
    ["Long-Term Leads (new)", c.long_term_leads_new, "long-term"],
    ["Property Owner Leads (new)", c.owner_leads_new, "owner"],
    ["Active Guests", c.active_guests, "guests"],
    ["New Guest Messages", c.new_guest_messages, "msgs"],
    ["Open Maintenance", c.open_maintenance, "maint"],
    ["Open Housekeeping", c.open_housekeeping, "house"],
    ["Upcoming Payments", c.upcoming_payments, "up"],
    ["Overdue Payments", c.overdue_payments, "over"],
    ["Extension Requests (pending)", c.extension_requests_pending, "ext"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="mgmt-summary">
      {tiles.map(([label, v, k]) => (
        <div key={k} className={`${card}`} data-testid={`summary-${k}`}>
          <p className="text-xs uppercase tracking-wider text-[#78716C]">{label}</p>
          <p className="mt-2 text-3xl font-light tracking-tighter text-[#1C1917]">{v ?? "—"}</p>
        </div>
      ))}
    </div>
  );
}

function Leads() {
  const [type, setType] = useState("longterm");
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/management/leads", { params: { inquiry_type: type } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [type]);
  const update = async (id, patch) => {
    setBusy(true);
    try { await api.patch(`/management/leads/${id}`, patch); await load(); toast.success("Lead updated."); }
    catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };
  const openLead = items.find((L) => L.id === openId) || null;
  return (
    <div data-testid="mgmt-leads">
      <div className="flex gap-2 mb-4">
        {[["longterm", "Long-Term"], ["owner", "Property Owner"]].map(([v, l]) => (
          <button key={v} onClick={() => setType(v)} data-testid={`leads-${v}`} className={`rounded-full px-4 py-1.5 text-sm border ${type === v ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-white border-[#E7E5E4] text-[#44403C]"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-[#78716C]">No leads.</p>}
        {items.map((L) => (
          <LeadCard key={L.id} lead={L} type={type} onOpen={() => setOpenId(L.id)} />
        ))}
      </div>
      <LeadDetailDrawer
        lead={openLead}
        type={type}
        onClose={() => setOpenId(null)}
        onUpdate={(patch) => update(openLead.id, patch)}
        busy={busy}
      />
    </div>
  );
}

function LeadCard({ lead, type, onOpen }) {
  const L = lead;
  const latestNote = L.internal_notes?.length ? L.internal_notes[L.internal_notes.length - 1] : null;
  return (
    <div className={card} data-testid={`lead-${L.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-medium text-[#1C1917]">{L.name} · <span className="text-[#44403C] font-light">{L.email}</span></p>
          <p className="text-xs text-[#78716C]">{L.phone || "—"} · Received {L.created_at?.slice(0, 10)}</p>
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-[#F5F5F4] text-[#44403C]" data-testid={`lead-status-${L.id}`}>{L.lead_status || "New"}</span>
      </div>
      {type === "longterm" && (
        <div className="mt-3 text-xs text-[#57534E] grid grid-cols-2 md:grid-cols-4 gap-2">
          <span>Check-in: {L.check_in_date || "—"}</span>
          <span>Length: {L.length_of_stay || "—"}</span>
          <span>Budget: {L.budget_range || "—"}</span>
          <span>Accommodation: {L.preferred_accommodation || "—"}</span>
        </div>
      )}
      {type === "owner" && (
        <div className="mt-3 text-xs text-[#57534E] grid grid-cols-2 md:grid-cols-4 gap-2">
          <span>City: {L.city || "—"}</span>
          <span>Beds/Baths: {L.bedrooms || "—"}/{L.bathrooms || "—"}</span>
          <span>Type: {L.property_type || "—"}</span>
          <span>Status: {L.property_status || "—"}</span>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#78716C]">
        <span>Follow-up: <span className="text-[#1C1917]">{L.follow_up_date || "—"}</span></span>
        {latestNote && (
          <span className="inline-flex items-center gap-1"><StickyNote className="w-3 h-3" strokeWidth={1.5} /> {latestNote.at?.slice(0, 10)} · {latestNote.by}: <span className="text-[#44403C]">{latestNote.text}</span></span>
        )}
      </div>
      <div className="mt-4">
        <button onClick={onOpen} data-testid={`view-details-${L.id}`} className={ghost + " text-xs"}>View details</button>
      </div>
    </div>
  );
}

function LeadDetailDrawer({ lead, type, onClose, onUpdate, busy }) {
  const [status, setStatus] = useState("");
  const [follow, setFollow] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (lead) {
      setStatus(lead.lead_status || "New");
      setFollow(lead.follow_up_date || "");
      setNote("");
    }
  }, [lead]);

  useEffect(() => {
    if (!lead) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [lead, onClose]);

  const save = async () => {
    const patch = { status, follow_up_date: follow, internal_note: note || null };
    await onUpdate(patch);
    setNote("");
  };

  return (
    <AnimatePresence>
      {lead && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#1C1917]/40 z-40" onClick={onClose} data-testid="lead-drawer-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed top-0 right-0 h-full w-full sm:w-[540px] max-w-full bg-white z-50 shadow-2xl overflow-y-auto"
            data-testid="lead-drawer"
          >
            <div className="sticky top-0 z-10 bg-white border-b border-[#E7E5E4] px-6 py-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] font-medium">
                  {type === "longterm" ? "Long-Term Stay Inquiry" : "Property Owner Lead"}
                </p>
                <h2 className="mt-1 text-xl font-normal tracking-tight text-[#1C1917]">{lead.name}</h2>
                <p className="text-xs text-[#78716C] mt-0.5">Submitted {lead.created_at?.replace("T", " ").slice(0, 16)}</p>
              </div>
              <button onClick={onClose} data-testid="lead-drawer-close" className="p-2 -mr-2 text-[#44403C] hover:text-[#1C1917]" aria-label="Close">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="px-6 py-6 space-y-6 text-sm">
              {/* Contact Actions */}
              <div className="flex flex-wrap gap-2" data-testid="lead-contact-actions">
                <a href={`mailto:${lead.email}`} data-testid="lead-email-guest" className={ghost + " text-xs inline-flex items-center gap-1.5"}>
                  <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> Email guest
                </a>
                {lead.phone && (
                  <a href={`tel:${lead.phone.replace(/[^+\d]/g, "")}`} data-testid="lead-call-guest" className={ghost + " text-xs inline-flex items-center gap-1.5"}>
                    <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> Call guest
                  </a>
                )}
              </div>

              {/* Guest Information */}
              <Section title="Guest information">
                <Field label="Full name" value={lead.name} />
                <Field label="Email" value={lead.email} />
                <Field label="Phone" value={lead.phone} />
                <Field label="Preferred contact method" value={lead.preferred_contact_method} />
              </Section>

              {type === "longterm" && (
                <>
                  <Section title="Stay information">
                    <Field label="Check-in date" value={lead.check_in_date} />
                    <Field label="Preferred move-out date" value={lead.check_out_date} />
                    <Field label="Expected length of stay" value={lead.length_of_stay} />
                    <Field label="Number of guests" value={lead.num_guests} />
                  </Section>
                  <Section title="Accommodation preferences">
                    <Field label="Preferred accommodation" value={lead.preferred_accommodation} />
                    <Field label="Preferred location" value={lead.preferred_location} />
                    <Field label="Bathroom preference" value={lead.bathroom_preference} />
                    <Field label="Budget range" value={lead.budget_range} />
                  </Section>
                  <Section title="Stay context">
                    <Field label="Reason for stay" value={lead.reason_for_stay} />
                    <Field label="How they heard about us" value={lead.referral_source} />
                  </Section>
                </>
              )}

              {type === "owner" && (
                <Section title="Property information">
                  <Field label="Partnership interest" value={lead.partnership_interest} />
                  <Field label="Property address" value={lead.property_address} />
                  <Field label="City" value={lead.city} />
                  <Field label="Property type" value={lead.property_type} />
                  <Field label="Bedrooms" value={lead.bedrooms} />
                  <Field label="Bathrooms" value={lead.bathrooms} />
                  <Field label="Currently furnished" value={lead.is_furnished} />
                  <Field label="Property status" value={lead.property_status} />
                </Section>
              )}

              {lead.message && (
                <Section title="Additional message / special requests">
                  <p className="text-[#44403C] whitespace-pre-wrap col-span-2">{lead.message}</p>
                </Section>
              )}

              <Section title="System information">
                <Field label="Lead type" value={type === "longterm" ? "Long-Term Stay Inquiry" : "Property Owner Lead"} />
                <Field label="Date submitted" value={lead.created_at?.replace("T", " ").slice(0, 16)} />
                <Field label="Current status" value={lead.lead_status || "New"} />
                <Field label="Follow-up date" value={lead.follow_up_date} />
              </Section>

              {/* CRM Controls */}
              <div className="rounded-2xl border border-[#E7E5E4] bg-[#F5F5F4] p-5" data-testid="lead-crm-controls">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] font-medium">CRM actions</p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#78716C]">Status</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lead-status-select" className={inp}>
                      {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#78716C]">Follow-up date</label>
                    <input type="date" value={follow} onChange={(e) => setFollow(e.target.value)} data-testid="lead-followup-input" className={inp} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-[#78716C]">Add internal note (private)</label>
                    <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notes are staff-only; never visible to the guest." data-testid="lead-note-input"
                      className="w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] px-3 py-2 text-[#1C1917] text-sm" />
                  </div>
                </div>
                <button disabled={busy} onClick={save} data-testid="lead-save-btn" className={btn + " mt-4"}>{busy ? "Saving…" : "Save changes"}</button>
              </div>

              {/* Note history */}
              {lead.internal_notes?.length > 0 && (
                <Section title="Internal note history (staff only)">
                  <div className="col-span-2 space-y-2">
                    {lead.internal_notes.slice().reverse().map((n, i) => (
                      <div key={i} className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-xs text-[#44403C]" data-testid={`lead-note-${i}`}>
                        <p className="text-[#78716C]">{n.at?.replace("T", " ").slice(0, 16)} · {n.by}</p>
                        <p className="mt-1 text-[#1C1917] whitespace-pre-wrap">{n.text}</p>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] font-medium mb-3">{title}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] text-[#78716C]">{label}</p>
      <p className="mt-0.5 text-[#1C1917] break-words">{value || "—"}</p>
    </div>
  );
}

function Guests() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/management/guests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const patch = async (id, body) => { try { await api.patch(`/management/stays/${id}/payment`, body); await load(); toast.success("Updated."); } catch (e) { toast.error(extractErr(e)); } };
  return (
    <div data-testid="mgmt-guests" className="space-y-3">
      {items.length === 0 && <p className="text-[#78716C]">No active guests yet. Create a guest account (Create User) and add a stay via the API/admin later.</p>}
      {items.map((s) => (
        <div key={s.id} className={card} data-testid={`stay-${s.id}`}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div><p className="text-xs text-[#78716C]">Guest</p><p>{s.guest_name}</p></div>
            <div><p className="text-xs text-[#78716C]">Unit</p><p>{s.unit_slug}</p></div>
            <div><p className="text-xs text-[#78716C]">Check-in → out</p><p>{s.check_in} → {s.check_out}</p></div>
            <div><p className="text-xs text-[#78716C]">Rate</p><p>${Number(s.monthly_rate).toLocaleString()}/mo</p></div>
            <div><p className="text-xs text-[#78716C]">Payment</p><p>{s.payment_status} · ${s.next_payment_amount || 0} · {s.next_payment_due || "—"}</p></div>
          </div>
          <div className="mt-3 flex gap-2 flex-wrap">
            {["Upcoming", "Due", "Paid", "Overdue"].map((st) => <button key={st} onClick={() => patch(s.id, { payment_status: st })} className={ghost + " text-xs"}>{st}</button>)}
          </div>
        </div>
      ))}
    </div>
  );
}

function Requests() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/management/requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const patch = async (id, body) => { try { await api.patch(`/management/requests/${id}`, body); await load(); toast.success("Updated."); } catch (e) { toast.error(extractErr(e)); } };
  return (
    <div data-testid="mgmt-requests" className="space-y-3">
      {items.length === 0 && <p className="text-[#78716C]">No requests.</p>}
      {items.map((r) => <ReqItem key={r.id} r={r} onPatch={(b) => patch(r.id, b)} />)}
    </div>
  );
}

function ReqItem({ r, onPatch }) {
  const [status, setStatus] = useState(r.status);
  const [pub, setPub] = useState("");
  const [prv, setPrv] = useState("");
  return (
    <div className={card} data-testid={`req-${r.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div><p className="font-medium text-[#1C1917]">{r.category} · <span className="text-[#78716C] font-light">{r.guest_name}</span></p><p className="text-xs text-[#78716C]">Unit: {r.unit_slug} · {r.created_at?.slice(0, 10)}</p></div>
        <span className="text-xs px-2 py-1 rounded-full bg-[#F5F5F4] text-[#44403C]">{r.status}</span>
      </div>
      <p className="mt-2 text-sm text-[#44403C]">{r.message}</p>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <div><label className="text-xs text-[#78716C]">Status</label><select value={status} onChange={(e) => setStatus(e.target.value)} className={inp}>{REQ_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
        <div><label className="text-xs text-[#78716C]">Guest-visible update</label><input value={pub} onChange={(e) => setPub(e.target.value)} placeholder="Visible to guest" className={inp} /></div>
        <div><label className="text-xs text-[#78716C]">Internal note</label><input value={prv} onChange={(e) => setPrv(e.target.value)} placeholder="Staff only" className={inp} /></div>
      </div>
      <button className={`${btn} mt-3`} onClick={() => { onPatch({ status, guest_visible_update: pub || null, internal_note: prv || null }); setPub(""); setPrv(""); }}>Save</button>
    </div>
  );
}

function Extensions() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/management/extensions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (id, status) => { try { await api.patch(`/management/extensions/${id}`, { status }); await load(); toast.success(status); } catch (e) { toast.error(extractErr(e)); } };
  return (
    <div data-testid="mgmt-extensions" className="space-y-3">
      {items.length === 0 && <p className="text-[#78716C]">No extension requests.</p>}
      {items.map((x) => (
        <div key={x.id} className={card} data-testid={`ext-${x.id}`}>
          <p className="font-medium text-[#1C1917]">{x.guest_name}</p>
          <p className="text-sm text-[#44403C]">{x.current_checkout} → {x.requested_checkout}</p>
          {x.message && <p className="mt-2 text-sm text-[#57534E]">{x.message}</p>}
          <div className="mt-3 flex gap-2">
            <span className="text-xs px-2 py-1 rounded-full bg-[#F5F5F4] text-[#44403C] mr-2">{x.status}</span>
            <button className={btn} onClick={() => decide(x.id, "Approved")}>Approve</button>
            <button className={ghost} onClick={() => decide(x.id, "Declined")}>Decline</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Users() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setItems(data); }
    catch (e) { toast.error(extractErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const resend = async (id) => {
    try {
      const { data } = await api.post(`/users/${id}/resend-invitation`);
      if (data.invitation_sent) toast.success("Invitation resent.");
      else toast.error(data.invitation_error || "Invitation could not be delivered. Please try again.");
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };
  return (
    <div className="space-y-6" data-testid="mgmt-users">
      <CreateUserForm onCreated={load} />
      <div className={card} data-testid="users-list">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Portal users</p>
          <button onClick={load} className={ghost + " text-xs"}>Refresh</button>
        </div>
        {loading && <p className="text-[#78716C] text-sm">Loading…</p>}
        {!loading && items.length === 0 && <p className="text-[#78716C] text-sm">No users yet.</p>}
        <div className="divide-y divide-[#E7E5E4]">
          {items.map((u) => (
            <div key={u.id} className="py-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`user-${u.id}`}>
              <div>
                <p className="text-[#1C1917] font-medium">{u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email}</p>
                <p className="text-xs text-[#78716C]">{u.email} · <span className="uppercase tracking-wider text-[#8A9A86]">{u.role}</span></p>
                {u.assigned_unit_slugs?.length > 0 && (
                  <p className="text-xs text-[#57534E] mt-1">Units: {u.assigned_unit_slugs.join(", ")}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={u.invitation_status} />
                {(u.invitation_status === "Pending" || u.invitation_status === "Expired") && u.active !== false && (
                  <button onClick={() => resend(u.id)} data-testid={`resend-${u.id}`} className={ghost + " text-xs"}>Resend invitation</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Active: "bg-[#E8EFE7] text-[#4A6A48]",
    Pending: "bg-[#F5F5F4] text-[#44403C]",
    Expired: "bg-[#FDF2E9] text-[#8A5A2B]",
    Disabled: "bg-[#F5F5F4] text-[#78716C] line-through",
  };
  const cls = map[status] || map.Pending;
  return <span className={`text-xs px-2 py-1 rounded-full ${cls}`} data-testid={`status-${status || "unknown"}`}>{status || "Pending"}</span>;
}

function CreateUserForm({ onCreated }) {
  const empty = { first_name: "", last_name: "", email: "", role: "guest", assigned_unit_slugs: "", send_invitation: true };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const slugs = f.assigned_unit_slugs.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        email: f.email.trim().toLowerCase(),
        role: f.role,
        assigned_unit_slugs: slugs,
        send_invitation: f.send_invitation,
      };
      const { data } = await api.post("/users", payload);
      if (f.send_invitation) {
        if (data.invitation_sent) toast.success(`User created and invitation email sent to ${payload.email}.`);
        else toast.warning(`User created, but invitation email could not be delivered. Please resend the invitation.`);
      } else {
        toast.success(`User created for ${payload.email}. No invitation was sent — you can resend later.`);
      }
      setF(empty);
      onCreated?.();
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };
  const needsUnit = f.role === "guest" || f.role === "homeowner";
  return (
    <form onSubmit={submit} className={card} data-testid="create-user-form">
      <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Admin · Create user</p>
      <p className="mt-1 text-sm text-[#78716C]">
        Invite-only. New users receive a secure email with a one-time activation link to set their own password.
      </p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <input required placeholder="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="new-first-name" className={inp} />
        <input placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="new-last-name" className={inp} />
        <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-email" className={`${inp} md:col-span-2`} />
        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="new-role" className={inp}>
          {ROLES_ALL.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <input placeholder={needsUnit ? "Assigned unit slug(s), comma-separated" : "Assigned unit(s) — optional for staff"} value={f.assigned_unit_slugs} onChange={(e) => setF({ ...f, assigned_unit_slugs: e.target.value })} data-testid="new-slugs" className={inp} />
      </div>
      <label className="mt-4 inline-flex items-center gap-2 text-sm text-[#44403C] cursor-pointer" data-testid="toggle-send-invitation">
        <input type="checkbox" checked={f.send_invitation} onChange={(e) => setF({ ...f, send_invitation: e.target.checked })} className="h-4 w-4 accent-[#1C1917]" />
        Send invitation email now
      </label>
      <div className="mt-4">
        <button type="submit" disabled={busy} data-testid="create-user-submit" className={btn}>{busy ? "Creating…" : "Create user"}</button>
      </div>
    </form>
  );
}


function Notifications() {
  const [s, setS] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newSmsLabel, setNewSmsLabel] = useState("");
  const [newSmsNumber, setNewSmsNumber] = useState("");

  const load = async () => {
    try {
      const [sr, lr] = await Promise.all([
        api.get("/admin/notification-settings"),
        api.get("/admin/notification-log"),
      ]);
      setS(sr.data); setLog(lr.data);
    } catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/notification-settings", patch);
      setS(data); toast.success("Notification settings updated.");
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };

  const addEmail = () => {
    const e = newRecipient.trim().toLowerCase();
    if (!e || !/^\S+@\S+\.\S+$/.test(e)) { toast.error("Enter a valid email address."); return; }
    const list = Array.from(new Set([...(s.email_recipients || []), e]));
    save({ email_recipients: list });
    setNewRecipient(""); setNewLabel("");
  };
  const removeEmail = (email) => {
    if ((s.email_recipients || []).length <= 1) { toast.error("At least one email recipient is required."); return; }
    save({ email_recipients: s.email_recipients.filter((x) => x !== email) });
  };
  const addSmsRecipient = () => {
    const num = newSmsNumber.trim();
    if (!/^\+\d{7,15}$/.test(num)) { toast.error("Phone must be in E.164 format, e.g. +15125550100"); return; }
    const list = [...(s.sms_recipients || []), { label: newSmsLabel.trim() || "Manager", e164: num, enabled: true }];
    save({ sms_recipients: list });
    setNewSmsLabel(""); setNewSmsNumber("");
  };
  const removeSmsRecipient = (i) => {
    const list = [...s.sms_recipients]; list.splice(i, 1);
    save({ sms_recipients: list });
  };
  const toggleSmsRecipient = (i) => {
    const list = s.sms_recipients.map((r, idx) => idx === i ? { ...r, enabled: !r.enabled } : r);
    save({ sms_recipients: list });
  };

  if (!s) return <p className="text-[#78716C]">Loading…</p>;

  return (
    <div className="space-y-5" data-testid="mgmt-notifications">
      <EmailSenderSettings />
      {/* Email */}
      <div className={card} data-testid="notif-email-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Email lead notifications</p>
            <h3 className="mt-1 text-lg font-normal text-[#1C1917]">Notify management by email on every new lead</h3>
            <p className="mt-1 text-sm text-[#78716C]">Sent instantly for Long-Term Stay and Property Owner inquiries. Lead is always saved to the CRM first, even if email delivery fails.</p>
          </div>
          <Toggle checked={!!s.email_enabled} onChange={(v) => save({ email_enabled: v })} testid="toggle-email" disabled={busy} />
        </div>
        <div className="mt-5">
          <p className="text-xs text-[#78716C] mb-2">Recipient addresses</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {(s.email_recipients || []).map((e) => (
              <span key={e} className="inline-flex items-center gap-2 text-xs rounded-full bg-[#F5F5F4] border border-[#E7E5E4] px-3 py-1.5 text-[#1C1917]" data-testid={`email-recipient-${e}`}>
                {e}
                <button onClick={() => removeEmail(e)} className="text-[#78716C] hover:text-[#1C1917]" aria-label={`Remove ${e}`}>
                  <X className="w-3 h-3" strokeWidth={2} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <input type="email" placeholder="add-another@masayahospitality.com" value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)} data-testid="new-email-recipient" className={inp + " max-w-sm"} />
            <button onClick={addEmail} disabled={busy} data-testid="add-email-recipient" className={ghost + " text-xs"}>Add recipient</button>
          </div>
        </div>
      </div>

      {/* SMS */}
      <div className={card + " opacity-100"} data-testid="notif-sms-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#8A9A86]">SMS lead notifications</p>
            <h3 className="mt-1 text-lg font-normal text-[#1C1917]">Text-message alerts on every new lead</h3>
            <p className="mt-1 text-sm text-[#78716C]">
              {s.sms_provider_configured
                ? "Provider connected. Toggle on to send short SMS alerts to authorized management numbers below."
                : "SMS notifications can be enabled after Twilio is configured. You can pre-add recipients below — they'll start receiving alerts as soon as the provider is connected."}
            </p>
          </div>
          <Toggle checked={!!s.sms_enabled} onChange={(v) => save({ sms_enabled: v })} testid="toggle-sms" disabled={busy || !s.sms_provider_configured} />
        </div>
        <div className="mt-5">
          <p className="text-xs text-[#78716C] mb-2">Authorized management numbers (E.164 format)</p>
          <div className="space-y-2 mb-3">
            {(s.sms_recipients || []).length === 0 && <p className="text-xs text-[#78716C]">No SMS recipients yet.</p>}
            {(s.sms_recipients || []).map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-[#E7E5E4] bg-[#F5F5F4] px-3 py-2 text-sm" data-testid={`sms-recipient-${i}`}>
                <div>
                  <p className="text-[#1C1917]">{r.label} <span className="text-[#78716C] font-mono">· {r.e164}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <Toggle checked={!!r.enabled} onChange={() => toggleSmsRecipient(i)} testid={`sms-toggle-${i}`} disabled={busy} small />
                  <button onClick={() => removeSmsRecipient(i)} className="text-[#78716C] hover:text-[#1C1917]" data-testid={`sms-remove-${i}`} aria-label="Remove">
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <input placeholder="Label (e.g. Owner)" value={newSmsLabel} onChange={(e) => setNewSmsLabel(e.target.value)} data-testid="new-sms-label" className={inp + " max-w-[180px]"} />
            <input placeholder="+15125550100" value={newSmsNumber} onChange={(e) => setNewSmsNumber(e.target.value)} data-testid="new-sms-number" className={inp + " max-w-[220px]"} />
            <button onClick={addSmsRecipient} disabled={busy} data-testid="add-sms-recipient" className={ghost + " text-xs"}>Add number</button>
          </div>
        </div>
      </div>

      {/* Delivery log */}
      <div className={card} data-testid="notif-log-card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Delivery log</p>
            <p className="text-xs text-[#78716C] mt-1">Most recent lead, payment-reminder and admin-alert email attempts (sent / skipped / failed).</p>
          </div>
          <button onClick={load} className={ghost + " text-xs"}>Refresh</button>
        </div>
        <div className="mt-4 divide-y divide-[#E7E5E4]">
          {log.length === 0 && <p className="text-sm text-[#78716C]">No delivery attempts yet.</p>}
          {log.slice(0, 25).map((l) => (
            <div key={l.id} className="py-2 flex items-center justify-between gap-3 text-xs" data-testid={`log-${l.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="uppercase tracking-wider text-[#8A9A86] w-14 shrink-0">{l.channel}</span>
                <span className={(l.status === "sent" ? "text-[#4A6A48]" : l.status === "failed" ? "text-red-600" : "text-[#78716C]") + " w-14 shrink-0"}>{l.status}</span>
                {l.lead_type && <span className="text-[#8A9A86] font-mono text-[10px] shrink-0" title="message type">{l.lead_type}</span>}
                <span className="text-[#57534E] truncate">{l.recipient || "—"}</span>
                {l.error && <span className="text-[#78716C] truncate max-w-[260px]" title={l.error}>· {l.error}</span>}
              </div>
              <span className="text-[#78716C] shrink-0">{l.at?.replace("T", " ").slice(0, 16)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled, testid, small }) {
  const w = small ? "w-9" : "w-11";
  const dot = small ? "w-3.5 h-3.5" : "w-4 h-4";
  const tx = small ? (checked ? "translate-x-5" : "translate-x-0.5") : (checked ? "translate-x-6" : "translate-x-0.5");
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`${w} h-6 rounded-full relative transition-colors ${checked ? "bg-[#1C1917]" : "bg-[#E7E5E4]"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <span className={`absolute top-1 ${dot} rounded-full bg-white transition-transform ${tx}`} />
    </button>
  );
}
